// Npm
import { Loader } from '@nt/dds-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import axios from 'axios';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Route, useHistory, Switch, useLocation } from 'react-router-dom';

// Local
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { store } from '../app/store';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Layout from '../components/Layout';
import { resetCreate } from '../pages/Create/CreateSlice';
import Home from '../pages/Home';
import { setAxiosData, resetUserDetails, setUserLoggedOut, selectFeatureFlags } from '../pages/Home/HomeSlice';
import { resetLegalEntity } from '../pages/LegalEntity/LegalEntitySlice';
import { getOIDCConfiguration } from '../utils/helper';
import oktaConfig from './config';
import OktaSessionTimeout from './OktaSessionTimeout';
import ProtectedRoute from './ProtectedRoute';
import './index.css';

const LegalEntity = React.lazy(() => import('../pages/LegalEntity'));
const Maintenance = React.lazy(() => import('../pages/Maintenance'));
const Create = React.lazy(() => import('../pages/Create'));
export const Routes: React.FC = () => {
  const storeDispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { sorryPage } = useAppSelector(selectFeatureFlags);
  const oktaAuth = useMemo(() => {
    const OIDConfigs = oktaConfig.config.okta.oidc;
    const { originMap } = oktaConfig.config;
    return new OktaAuth(getOIDCConfiguration(OIDConfigs, originMap));
  }, []);
  const maintenanceExceptionRoutes = ['/login/callback', '/maintenance'];
  console.log('Okta Config ==>', oktaConfig, process.env.NODE_ENV, process.env.UXP_MODE, oktaAuth);

  const restoreOriginalUri = useCallback(
    (_oktaAuth: any, originalUri: any) => {
      history.replace(toRelativeUrl(originalUri ?? '/', window.location.origin));
    },
    [history]
  );

  const handleLogout = useCallback(() => {
    storeDispatch(setUserLoggedOut(true));
    setTimeout(() => {
      storeDispatch(resetUserDetails());
      storeDispatch(resetLegalEntity());
      storeDispatch(resetCreate());
    }, 1000);
  }, [storeDispatch]);

  useEffect(() => {
    if (sorryPage && !maintenanceExceptionRoutes.includes(location.pathname)) {
      history.replace('/maintenance');
    }
  }, [sorryPage, history, location.pathname]);

  useEffect(() => {
    axios.interceptors.request.use(
      (config: any) => {
        storeDispatch(setAxiosData({ showLoader: true }));
        const id = store.getState().home?.userDetails?.personaId;
        config.headers.Personaid = id;
        const oktaToken = oktaAuth.getAccessToken();
        config.headers.Authorization = `Bearer ${oktaToken}`;
        return config;
      },
      (error: any) => {
        storeDispatch(setAxiosData({ showLoader: false }));
        return Promise.reject(new Error('Network timeout', error));
      }
    );

    axios.interceptors.response.use(
      response => {
        storeDispatch(setAxiosData({ showLoader: false }));
        return response;
      },
      (error: any) => {
        if (error.name === 'AxiosError') {
          storeDispatch(setAxiosData({ showError: true }));
        }
        storeDispatch(setAxiosData({ showLoader: false }));
        return Promise.reject(new Error('Network timeout', error));
      }
    );
    return () => {
      axios.interceptors.request.clear();
      axios.interceptors.response.clear();
    };
  }, []);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <div className="display-flex flex-column minheight-100vh bgcolor-grey-100">
        <Header />
        <Layout>
          <>
            <div className="fluid-routing-content-wrap">
              <Switch>
                <Suspense fallback={<Loader ariaLabel="Loading" size="lg" />}>
                  <ProtectedRoute path={'/'} exact component={Home} />
                  <ProtectedRoute path={'/legalentity'} component={LegalEntity} />
                  <ProtectedRoute path={'/create'} component={Create} />
                  <ProtectedRoute path={'/maintenance'} component={Maintenance} />
                  <Route path="/login/callback" exact component={LoginCallback} />
                </Suspense>
              </Switch>
              <OktaSessionTimeout logoutFn={handleLogout} />
            </div>
            <Footer />
          </>
        </Layout>
      </div>
    </Security>
  );
};

export default () => (
  <Router>
    <Routes />
  </Router>
);
